import * as React from "react";

import { KEY_INFO } from "@config/testIds/keyInfo";
import { VALIDATE_FIELD_TEXTAREA_LIMIT_LONG } from "@config/consts";

import { KEY_PICK } from "@typings/booking";
import { KEY_PICK_B2B } from "@typings/b2b";

import { Margins, Radio, Input, TestWrapper } from "@ui/Atoms";
import { InfoMessage } from "@ui/Molecules/InfoMessage";

export enum KEY_PICKER_FIELDS {
  KEY_PICK = "keyPick",
  LOCATION_COMMENT = "locationComment",
  SPECIAL_INSTRUCTIONS = "specialInstructions",
  APPLY_ONWARDS = "applyOnwards",
}

export type KeyPickChangeEvent = React.ChangeEvent<HTMLInputElement>;

interface Props {
  onRadioChange: (e: KeyPickChangeEvent, label?: string) => void;
  onCommentChange: (e: KeyPickChangeEvent) => void;
  values: {
    [KEY_PICKER_FIELDS.KEY_PICK]: KEY_PICK | KEY_PICK_B2B;
    [KEY_PICKER_FIELDS.LOCATION_COMMENT]: string;
  };
  translations: {
    firstOptionText: string;
    secondOptionText: string;
    thirdOptionText?: string;
    tellUsMoreLabelText: string;
    infoMessageText?: string;
  };
  hideOptions?: (KEY_PICK | KEY_PICK_B2B)[];
  isB2BVariant?: boolean;
}

const KeyPickerRadios = (props: Props): React.ReactElement => {
  const optionToHide = props.hideOptions || [];

  if (props.isB2BVariant) {
    return (
      <React.Fragment>
        {!optionToHide.includes(KEY_PICK_B2B.SOMEONE_WILL_BE_THERE) && (
          <TestWrapper testId={KEY_INFO.SOMEONE_AT_HOME}>
            <Radio
              label={props.translations.firstOptionText}
              name={KEY_PICKER_FIELDS.KEY_PICK}
              value={KEY_PICK_B2B.SOMEONE_WILL_BE_THERE}
              id="key-info-1"
              onChange={e => {
                props.onRadioChange(e, props.translations.firstOptionText);
              }}
              checked={
                String(props.values[KEY_PICKER_FIELDS.KEY_PICK]) ===
                String(KEY_PICK_B2B.SOMEONE_WILL_BE_THERE)
              }
            />
          </TestWrapper>
        )}

        {!optionToHide.includes(KEY_PICK_B2B.KEY_WILL_BE_PROVIDED) && (
          <TestWrapper testId={KEY_INFO.HIDE_KEY}>
            <Radio
              label={props.translations.secondOptionText}
              name={KEY_PICKER_FIELDS.KEY_PICK}
              value={KEY_PICK_B2B.KEY_WILL_BE_PROVIDED}
              id="key-info-2"
              onChange={e => {
                props.onRadioChange(e, props.translations.secondOptionText);
              }}
              checked={
                props.values[KEY_PICKER_FIELDS.KEY_PICK] ===
                String(KEY_PICK_B2B.KEY_WILL_BE_PROVIDED)
              }
            />
          </TestWrapper>
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {!optionToHide.includes(KEY_PICK.SOMEONE_AT_HOME) && (
        <TestWrapper testId={KEY_INFO.SOMEONE_AT_HOME}>
          <Radio
            label={props.translations.firstOptionText}
            name={KEY_PICKER_FIELDS.KEY_PICK}
            value={KEY_PICK.SOMEONE_AT_HOME}
            id="key-info-1"
            onChange={e => {
              props.onRadioChange(e, props.translations.firstOptionText);
            }}
            checked={
              String(props.values[KEY_PICKER_FIELDS.KEY_PICK]) ===
              String(KEY_PICK.SOMEONE_AT_HOME)
            }
          />
        </TestWrapper>
      )}

      {!optionToHide.includes(KEY_PICK.CONCIERGE) && (
        <TestWrapper testId={KEY_INFO.CONCIERGE}>
          <Radio
            label={props.translations.secondOptionText}
            name={KEY_PICKER_FIELDS.KEY_PICK}
            value={KEY_PICK.CONCIERGE}
            id="key-info-2"
            onChange={e => {
              props.onRadioChange(e, props.translations.secondOptionText);
            }}
            checked={
              String(props.values[KEY_PICKER_FIELDS.KEY_PICK]) ===
              String(KEY_PICK.CONCIERGE)
            }
          />
        </TestWrapper>
      )}

      {!optionToHide.includes(KEY_PICK.HIDE_KEY) && (
        <TestWrapper testId={KEY_INFO.HIDE_KEY}>
          <Radio
            label={props.translations.thirdOptionText}
            name={KEY_PICKER_FIELDS.KEY_PICK}
            value={KEY_PICK.HIDE_KEY}
            id="key-info-3"
            onChange={e => {
              props.onRadioChange(e, props.translations.thirdOptionText);
            }}
            checked={
              String(props.values[KEY_PICKER_FIELDS.KEY_PICK]) ===
              String(KEY_PICK.HIDE_KEY)
            }
          />
        </TestWrapper>
      )}
    </React.Fragment>
  );
};

const KeyPicker = (props: Props): React.ReactElement => {
  return (
    <React.Fragment>
      <KeyPickerRadios {...props} />
      <Margins xs={["base_x4", null, null, null]}>
        <div>
          <TestWrapper testId={KEY_INFO.LOCATION_COMMENT}>
            <Input
              as="textarea"
              label={props.translations.tellUsMoreLabelText}
              onChange={props.onCommentChange}
              name={KEY_PICKER_FIELDS.LOCATION_COMMENT}
              value={props.values[KEY_PICKER_FIELDS.LOCATION_COMMENT]}
              maxLength={VALIDATE_FIELD_TEXTAREA_LIMIT_LONG}
            />
          </TestWrapper>
          {props.translations.infoMessageText && (
            <Margins xs={["base_x2", null, null, null]}>
              <div>
                <InfoMessage message={props.translations.infoMessageText} />
              </div>
            </Margins>
          )}
        </div>
      </Margins>
    </React.Fragment>
  );
};

export { KeyPicker };
